<template>
  <div class="statistics-page">
    <CommonOverlay :show="loading" title="Loading Statistics" message="Please wait..." />

    <!-- Page Header -->
    <div class="statistics-header">
      <div class="header-left">
        <h1 class="page-title">Campaign Statistics</h1>
        <p class="page-subtitle">Analyze your email, SMS, and AI-call campaign performance</p>
      </div>
      <div class="header-actions">
        <b-button variant="outline-primary" @click="openGenerateReportModal">
          <i class="fa fa-download mr-2"></i> Generate Report
        </b-button>
      </div>
    </div>

    <!-- Sub-navigation for Campaigns section -->
    <div class="campaigns-subnav">
      <router-link to="/app/campaigns" class="subnav-link">
        <i class="fa fa-bullhorn"></i>
        <span>Campaigns</span>
      </router-link>
      <router-link to="/app/campaigns-drafts" class="subnav-link">
        <i class="fa fa-pencil-square-o"></i>
        <span>Drafts</span>
      </router-link>
      <router-link
        to="/app/campaigns-statistics"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-statistics' }"
      >
        <i class="fa fa-bar-chart"></i>
        <span>Reports</span>
      </router-link>
      <router-link to="/app/campaigns-templates" class="subnav-link">
        <i class="fa fa-file-text-o"></i>
        <span>Templates</span>
      </router-link>
      <router-link to="/app/campaigns-ab-tests" class="subnav-link">
        <i class="fa fa-flask"></i>
        <span>A/B Tests</span>
      </router-link>
    </div>

    <!-- Error State -->
    <div v-if="storeError && !loading" class="error-banner">
      <i class="fa fa-exclamation-triangle mr-2"></i>
      <span>Couldn't load statistics. {{ storeError }}</span>
      <b-button size="sm" variant="outline-danger" class="ml-3" @click="loadData">
        <i class="fa fa-refresh mr-1"></i> Retry
      </b-button>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filters-row">
        <!-- Date Range Picker -->
        <div class="filter-group">
          <label>Date Range</label>
          <b-form-select v-model="dateRange" :options="dateRangeOptions" class="filter-select"></b-form-select>
        </div>

        <!-- Message Filter (client-side, filters the Messages Sent table) -->
        <div class="filter-group">
          <label>Message Type</label>
          <b-form-select v-model="messageFilter" :options="messageFilterOptions" class="filter-select"></b-form-select>
        </div>
      </div>
    </div>

    <!-- Stats Cards Grid -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-header">
          <i class="fa fa-check-circle text-success"></i>
          <span class="stat-label">Delivery Rate</span>
        </div>
        <div class="stat-value">{{ overall.deliveryRate || 0 }}%</div>
        <div class="stat-detail">
          {{ (overall.totalDelivered || 0) | formatNumber }} delivered
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <i class="fa fa-envelope-open text-primary"></i>
          <span class="stat-label">Open Rate</span>
        </div>
        <div class="stat-value">{{ overall.openRate || 0 }}%</div>
        <div class="stat-detail">
          {{ (overall.totalOpened || 0) | formatNumber }} opened
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <i class="fa fa-mouse-pointer text-info"></i>
          <span class="stat-label">Click Rate</span>
        </div>
        <div class="stat-value">{{ overall.clickRate || 0 }}%</div>
        <div class="stat-detail">
          {{ (overall.totalClicked || 0) | formatNumber }} clicked
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <i class="fa fa-percentage text-warning"></i>
          <span class="stat-label">CTOR</span>
        </div>
        <div class="stat-value">{{ overall.clickToOpenRate || 0 }}%</div>
        <div class="stat-detail">
          Click-to-Open Rate
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-header">
          <i class="fa fa-exclamation-triangle text-danger"></i>
          <span class="stat-label">Bounce Rate</span>
        </div>
        <div class="stat-value">{{ overall.bounceRate || 0 }}%</div>
        <div class="stat-detail">
          {{ (overall.totalBounced || 0) | formatNumber }} bounced
        </div>
      </div>
    </div>

    <!-- Per-Channel Summary Section -->
    <div class="channel-summary-section">
      <h5 class="section-title"><i class="fa fa-layer-group mr-2"></i>Performance by Channel</h5>
      <div class="channel-grid">
        <div class="channel-cell" v-for="card in channelCards" :key="card.key">
          <!-- Not enabled → upsell. AI Call isn't launched → Coming soon; SMS / Email → request-to-enable. -->
          <FeaturePreview
            v-if="!card.enabled && card.key === 'ai_call'"
            data-test="stats-ai-call-upsell"
            :title="card.upsell.title"
            :description="card.upsell.description"
            :icon="card.icon"
            coming-soon
          />
          <FeaturePreview
            v-else-if="!card.enabled && card.key === 'sms'"
            data-test="stats-sms-upsell"
            :title="card.upsell.title"
            :description="card.upsell.description"
            :icon="card.icon"
            feature-key="SMS"
            feature-label="2-Way SMS texting"
            source-page="Campaign Statistics - SMS"
          />
          <FeaturePreview
            v-else-if="!card.enabled && card.key === 'email'"
            data-test="stats-email-upsell"
            :title="card.upsell.title"
            :description="card.upsell.description"
            :icon="card.icon"
            feature-key="EMAIL"
            feature-label="Patient email"
            source-page="Campaign Statistics - Email"
          />

          <!-- Enabled → real stats card -->
          <div v-else class="channel-card">
            <div class="channel-card-header">
              <i :class="[card.icon, card.iconColor]"></i>
              <span class="channel-card-title">{{ card.label }}</span>
              <b-badge v-if="card.includedBadge" variant="light" class="ml-auto included-badge">Included</b-badge>
            </div>

            <template v-if="card.data">
              <div class="channel-stats">
                <div class="cstat">
                  <span class="cstat-val">{{ (card.data.sent || 0) | formatNumber }}</span>
                  <span class="cstat-label">Sent</span>
                </div>
                <div class="cstat">
                  <span class="cstat-val">{{ (card.data.delivered || 0) | formatNumber }}</span>
                  <span class="cstat-label">Delivered</span>
                </div>
                <div class="cstat">
                  <span class="cstat-val">{{ (card.data.failed || 0) | formatNumber }}</span>
                  <span class="cstat-label">Failed</span>
                </div>
              </div>

              <div class="channel-rates">
                <span class="crate">Delivery <strong>{{ card.data.deliveryRate }}%</strong></span>
                <span class="crate" v-if="card.showEngagement">Open <strong>{{ card.data.openRate }}%</strong></span>
                <span class="crate" v-if="card.showEngagement">Click <strong>{{ card.data.clickRate }}%</strong></span>
              </div>

              <!-- SMS is the only credit-metered channel -->
              <div v-if="card.showCredits" class="channel-credits">
                <i class="fa fa-comment-dots mr-1"></i> Credits used: {{ (card.data.creditsSpent || 0) | formatNumber }}
              </div>
            </template>

            <!-- Enabled but no sends in range -->
            <div v-else class="channel-empty">
              <i class="fa fa-inbox"></i>
              <span>No {{ card.label }} sends in this range</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Email Domains -->
    <div class="charts-section">
      <div class="chart-card full-width">
        <div class="chart-header">
          <h5><i class="fa fa-at mr-2"></i>Top Email Domains</h5>
        </div>
        <div class="chart-body">
          <canvas v-if="byDomain.length" ref="domainsChart"></canvas>
          <div v-else class="empty-chart-state">
            <i class="fa fa-at"></i>
            <p>No email domain data yet</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Messages Sent Table -->
    <div class="messages-table-section">
      <div class="section-header">
        <h5><i class="fa fa-paper-plane mr-2"></i>Messages Sent</h5>
        <div class="table-search">
          <i class="fa fa-search"></i>
          <input
            type="text"
            v-model="tableSearch"
            placeholder="Search campaigns..."
            class="search-input"
          />
        </div>
      </div>

      <div class="messages-table-container">
        <table class="messages-table">
          <thead>
            <tr>
              <th class="sortable" @click="sortTable('Name')">
                Name
                <i :class="getTableSortIcon('Name')"></i>
              </th>
              <th class="sortable" @click="sortTable('Channel')">
                Channel
                <i :class="getTableSortIcon('Channel')"></i>
              </th>
              <th class="sortable" @click="sortTable('SentAt')">
                Sent on
                <i :class="getTableSortIcon('SentAt')"></i>
              </th>
              <th class="sortable" @click="sortTable('Delivered')">
                Delivered
                <i :class="getTableSortIcon('Delivered')"></i>
              </th>
              <th class="sortable" @click="sortTable('openRate')">
                Open Rate
                <i :class="getTableSortIcon('openRate')"></i>
              </th>
              <th class="sortable" @click="sortTable('clickRate')">
                Click Rate
                <i :class="getTableSortIcon('clickRate')"></i>
              </th>
              <th class="sortable" @click="sortTable('bounceRate')">
                Bounce Rate
                <i :class="getTableSortIcon('bounceRate')"></i>
              </th>
              <th>Credits</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="campaign in filteredTableCampaigns"
              :key="campaign.CampaignID"
              class="clickable-row"
              @click="viewCampaignDetail(campaign)"
            >
              <td class="campaign-name">
                <i :class="getTypeIcon(campaign.Channel)" class="mr-2"></i>
                {{ campaign.Name }}
              </td>
              <td>{{ channelLabel(campaign.Channel) }}</td>
              <td>{{ campaign.SentAt | dateformat('MM/DD/YYYY') }}</td>
              <td>{{ campaign.Delivered | formatNumber }}</td>
              <td>
                <span class="rate-badge" :class="getRateClass(campaign.openRate)">
                  {{ campaign.openRate }}%
                </span>
              </td>
              <td>
                <span class="rate-badge" :class="getRateClass(campaign.clickRate, 'click')">
                  {{ campaign.clickRate }}%
                </span>
              </td>
              <td>
                <span class="rate-badge" :class="getBounceRateClass(campaign.bounceRate)">
                  {{ campaign.bounceRate }}%
                </span>
              </td>
              <td>
                <span class="credits-cell" :class="creditsCellClass(campaign.Channel)">
                  {{ creditsDisplay(campaign) }}
                </span>
              </td>
              <td @click.stop>
                <b-dropdown variant="link" no-caret right toggle-class="action-btn">
                  <template #button-content>
                    <i class="fa fa-ellipsis-v"></i>
                  </template>
                  <b-dropdown-item @click="viewCampaignDetail(campaign)">
                    <i class="fa fa-eye mr-2"></i> View Details
                  </b-dropdown-item>
                  <b-dropdown-item @click="viewCampaignReport(campaign)">
                    <i class="fa fa-bar-chart mr-2"></i> Full Report
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="filteredTableCampaigns.length === 0" class="empty-table-state">
          <i class="fa fa-inbox"></i>
          <p>No sent campaigns yet</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="table-pagination" v-if="tableTotalPages > 1">
        <span class="pagination-info">
          Showing {{ tableStartItem }}-{{ tableEndItem }} of {{ sortedTableCampaigns.length }}
        </span>
        <div class="pagination-controls">
          <button :disabled="tablePage === 1" @click="tablePage--">
            <i class="fa fa-chevron-left"></i>
          </button>
          <span>Page {{ tablePage }} of {{ tableTotalPages }}</span>
          <button :disabled="tablePage === tableTotalPages" @click="tablePage++">
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Generate Report Modal -->
    <GenerateReportModal
      :visible.sync="showGenerateReportModal"
      :date-range="dateRange"
      @generate="handleGenerateReport"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { mapState } from 'vuex';

import { hasSmsNumber, isEmailEnabled } from '@/utils/siteCapability';import moment from 'moment';
import CommonOverlay from '@/components/CommonOverlay';
import FeaturePreview from '@/components/FeaturePreview/FeaturePreview.vue';
import GenerateReportModal from './components/GenerateReportModal.vue';
import Chart from 'chart.js';

export default {
  name: 'Statistics',

  components: {
    CommonOverlay,
    FeaturePreview,
    GenerateReportModal
  },

  data() {
    return {
      loading: false,

      // Filters
      dateRange: 'all',
      messageFilter: 'all',

      // Filter options
      dateRangeOptions: [
        { value: 'all', text: 'All time' },
        { value: 'last7', text: 'Last 7 days' },
        { value: 'last30', text: 'Last 30 days' },
        { value: 'last90', text: 'Last 90 days' },
        { value: 'thisMonth', text: 'This month' },
        { value: 'lastMonth', text: 'Last month' },
        { value: 'thisYear', text: 'This year' }
      ],
      messageFilterOptions: [
        { value: 'all', text: 'All messages' },
        { value: 'email', text: 'Email only' },
        { value: 'sms', text: 'SMS only' },
        { value: 'ai_call', text: 'AI Call only' }
      ],

      // Table state
      tableSearch: '',
      tableSortField: 'SentAt',
      tableSortDirection: 'desc',
      tablePage: 1,
      tablePerPage: 10,

      // Modal
      showGenerateReportModal: false,

      // Chart instance (email domains — the only chart backed by real data)
      domainsChartInstance: null
    };
  },

  computed: {
    ...mapState('campaigns', { siteStats: 'siteStats', storeError: 'error' }),
    ...mapState('customer', ['settings']),

    // --- Real stats slices (safe defaults while siteStats is null) ---
    overall() {
      return (this.siteStats && this.siteStats.overall) || {};
    },

    byChannel() {
      return (this.siteStats && this.siteStats.byChannel) || {};
    },

    byCampaign() {
      return (this.siteStats && this.siteStats.byCampaign) || [];
    },

    byDomain() {
      return (this.siteStats && this.siteStats.byDomain) || [];
    },

    // --- Channel enablement from site settings (same signal every SMS surface uses) ---
    // hasSmsNumber: this card REPORTS past SMS campaigns. A WhatsApp-only office keeps its
    // history and must not be shown an "Enable SMS Campaigns" upsell for texting it has.
    smsEnabled() {
      return hasSmsNumber({ GENERAL_PHONE: this.settingValue('GENERAL_PHONE') });
    },

    emailEnabled() {
      return isEmailEnabled({ GENERAL_VERIFIED_EMAILS: this.settingValue('GENERAL_VERIFIED_EMAILS') });
    },

    aiCallEnabled() {
      return this.settingValue('AI_FOLLOWUP_PAID_FOR') === 'TRUE';
    },

    // Per-channel cards; a channel that isn't enabled renders the FeaturePreview upsell instead.
    channelCards() {
      const bc = this.byChannel;
      return [
        {
          key: 'sms',
          label: 'SMS',
          icon: 'fa fa-comment',
          iconColor: 'text-success',
          enabled: this.smsEnabled,
          data: bc.sms || null,
          showEngagement: true,
          showCredits: true,
          upsell: {
            title: 'Enable SMS Campaigns',
            description: 'Text appointment reminders, recalls, and promotions to your patients.',
          }
        },
        {
          key: 'email',
          label: 'Email',
          icon: 'fa fa-envelope',
          iconColor: 'text-primary',
          enabled: this.emailEnabled,
          data: bc.email || null,
          showEngagement: true,
          includedBadge: true,
          upsell: {
            title: 'Enable Email Campaigns',
            description: 'Send branded newsletters and reminders to your patient list.',
          }
        },
        {
          key: 'ai_call',
          label: 'AI Call',
          icon: 'fa fa-phone',
          iconColor: 'text-info',
          enabled: this.aiCallEnabled,
          data: bc.ai_call || null,
          showEngagement: false,
          upsell: {
            title: 'Enable AI Call Campaigns',
            description: 'Reach patients with automated AI voice calls for recalls and reminders.',
          }
        }
      ];
    },

    sortedTableCampaigns() {
      let filtered = [...this.byCampaign];

      // Apply search filter
      if (this.tableSearch) {
        const query = this.tableSearch.toLowerCase();
        filtered = filtered.filter(c => (c.Name || '').toLowerCase().includes(query));
      }

      // Apply message type filter (on the campaign Channel/Type)
      if (this.messageFilter !== 'all') {
        filtered = filtered.filter(c => {
          if (this.messageFilter === 'email') {
            return c.Channel === 'Email' || c.Channel === 'Both';
          }
          if (this.messageFilter === 'sms') {
            return c.Channel === 'SMS' || c.Channel === 'Both';
          }
          if (this.messageFilter === 'ai_call') {
            return c.Channel === 'ai_call';
          }
          return true;
        });
      }

      // Sort — rates arrive as strings ("42.10") so numeric fields are parsed before comparing.
      const numericFields = ['Sent', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'openRate', 'clickRate', 'bounceRate', 'creditsSpent'];
      const field = this.tableSortField;
      filtered.sort((a, b) => {
        let aVal = a[field];
        let bVal = b[field];

        if (field === 'SentAt') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        } else if (numericFields.includes(field)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        if (aVal === bVal) return 0;
        if (this.tableSortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });

      return filtered;
    },

    filteredTableCampaigns() {
      const start = (this.tablePage - 1) * this.tablePerPage;
      const end = start + this.tablePerPage;
      return this.sortedTableCampaigns.slice(start, end);
    },

    tableTotalPages() {
      return Math.ceil(this.sortedTableCampaigns.length / this.tablePerPage);
    },

    tableStartItem() {
      return (this.tablePage - 1) * this.tablePerPage + 1;
    },

    tableEndItem() {
      const end = this.tablePage * this.tablePerPage;
      return end > this.sortedTableCampaigns.length ? this.sortedTableCampaigns.length : end;
    }
  },

  watch: {
    dateRange() {
      this.loadData();
    },
    tableSearch() {
      this.tablePage = 1;
    },
    messageFilter() {
      this.tablePage = 1;
    }
  },

  mounted() {
    this.loadData();
  },

  beforeDestroy() {
    this.destroyCharts();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.$store.dispatch('campaigns/fetchSiteStats', this.dateRangeParams());
      } catch (error) {
        console.error('Error loading statistics:', error);
      } finally {
        this.loading = false;
        this.$nextTick(() => this.renderDomainsChart());
      }
    },

    // Translate the date-range preset into startDate/endDate query params for fetchSiteStats.
    // 'all' → no params (full history).
    dateRangeParams() {
      const range = this.dateRange;
      if (!range || range === 'all') return {};

      if (range === 'lastMonth') {
        return {
          startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        };
      }

      let start;
      switch (range) {
        case 'last7': start = moment().subtract(7, 'days'); break;
        case 'last30': start = moment().subtract(30, 'days'); break;
        case 'last90': start = moment().subtract(90, 'days'); break;
        case 'thisMonth': start = moment().startOf('month'); break;
        case 'thisYear': start = moment().startOf('year'); break;
        default: return {};
      }
      return {
        startDate: start.format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
      };
    },

    settingValue(property) {
      const item = (this.settings || []).find(s => s && s.Property === property);
      return item && item.PropertyValue != null ? String(item.PropertyValue) : '';
    },

    destroyCharts() {
      if (this.domainsChartInstance) {
        this.domainsChartInstance.destroy();
        this.domainsChartInstance = null;
      }
    },

    renderDomainsChart() {
      this.destroyCharts();

      const domains = this.byDomain;
      if (!domains.length) return;

      const ctx = this.$refs.domainsChart && this.$refs.domainsChart.getContext('2d');
      if (!ctx) return;

      this.domainsChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: domains.map(d => d.domain),
          datasets: [
            {
              label: 'Recipients',
              data: domains.map(d => d.count),
              backgroundColor: [
                'rgba(0, 123, 255, 0.8)',
                'rgba(40, 167, 69, 0.8)',
                'rgba(255, 193, 7, 0.8)',
                'rgba(23, 162, 184, 0.8)',
                'rgba(111, 66, 193, 0.8)',
                'rgba(253, 126, 20, 0.8)',
                'rgba(108, 117, 125, 0.8)'
              ],
              borderRadius: 4
            }
          ]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            y: {
              grid: {
                display: false
              }
            }
          }
        }
      });
    },

    sortTable(field) {
      if (this.tableSortField === field) {
        this.tableSortDirection = this.tableSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.tableSortField = field;
        this.tableSortDirection = 'desc';
      }
      this.tablePage = 1;
    },

    getTableSortIcon(field) {
      if (this.tableSortField !== field) {
        return 'fa fa-sort';
      }
      return this.tableSortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    channelLabel(channel) {
      const labels = {
        'SMS': 'SMS',
        'Email': 'Email',
        'Both': 'SMS + Email',
        'ai_call': 'AI Call'
      };
      return labels[channel] || channel || '—';
    },

    getTypeIcon(channel) {
      const icons = {
        'SMS': 'fa fa-comment text-success',
        'Email': 'fa fa-envelope text-primary',
        'Both': 'fa fa-comments text-info',
        'ai_call': 'fa fa-phone text-info'
      };
      return icons[channel] || 'fa fa-paper-plane';
    },

    // Cost display rule: SMS/Both → credits number; Email → "Included"; AI-Call → "—" (outcomes only).
    creditsDisplay(campaign) {
      const channel = campaign.Channel;
      if (channel === 'SMS' || channel === 'Both') {
        return Number(campaign.creditsSpent || 0).toLocaleString();
      }
      if (channel === 'Email') {
        return 'Included';
      }
      return '—';
    },

    creditsCellClass(channel) {
      if (channel === 'SMS' || channel === 'Both') return 'credits-sms';
      if (channel === 'Email') return 'credits-included';
      return 'credits-none';
    },

    getRateClass(rate, type = 'open') {
      const r = parseFloat(rate) || 0;
      if (type === 'open') {
        if (r >= 50) return 'excellent';
        if (r >= 30) return 'good';
        if (r >= 15) return 'average';
        return 'low';
      }
      // Click rate thresholds
      if (r >= 15) return 'excellent';
      if (r >= 8) return 'good';
      if (r >= 3) return 'average';
      return 'low';
    },

    getBounceRateClass(rate) {
      const r = parseFloat(rate) || 0;
      if (r <= 1) return 'excellent';
      if (r <= 3) return 'good';
      if (r <= 5) return 'average';
      return 'poor';
    },

    openGenerateReportModal() {
      this.showGenerateReportModal = true;
    },

    handleGenerateReport(config) {
      console.log('Generating report with config:', config);
      this.$toast?.success?.('Report generated successfully');
    },

    viewCampaignDetail(campaign) {
      this.$router.push({
        name: 'campaignDetail',
        params: { campaignId: campaign.CampaignID }
      });
    },

    viewCampaignReport(campaign) {
      this.$router.push({
        name: 'campaignDetail',
        params: { campaignId: campaign.CampaignID },
        query: { tab: 'reports' }
      });
    }
  },

  filters: {
    formatNumber(value) {
      if (!value) return '0';
      return value.toLocaleString();
    }
  }
};
</script>

<style lang="scss" scoped>
.statistics-page {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.statistics-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  .page-subtitle {
    color: #6c757d;
    margin: 4px 0 0;
    font-size: 14px;
  }
}

// Sub-navigation for Campaigns section
.campaigns-subnav {
  display: flex;
  gap: 4px;
  background: white;
  padding: 6px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow-x: auto;

  .subnav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
    transition: all 0.2s ease;

    i {
      font-size: 14px;
    }

    &:hover {
      background: #f1f5f9;
      color: #334155;
    }

    &.active,
    &.router-link-exact-active {
      background: linear-gradient(135deg, #3abf94, #2d9474);
      color: white;
      box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);
    }
  }

  @media (max-width: 576px) {
    .subnav-link {
      padding: 8px 12px;
      font-size: 0.85rem;

      span {
        display: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.error-banner {
  display: flex;
  align-items: center;
  background: #fdeceb;
  border: 1px solid #f2c4c0;
  color: #b23b2f;
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 14px;
}

.filters-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .filters-row {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    min-width: 150px;

    label {
      font-size: 12px;
      font-weight: 500;
      color: #6c757d;
      margin-bottom: 4px;
      text-transform: uppercase;
    }

    .filter-select {
      border-radius: 6px;
    }
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  .stat-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    .stat-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      i {
        font-size: 16px;
      }

      .stat-label {
        font-size: 13px;
        font-weight: 500;
        color: #6c757d;
      }
    }

    .stat-value {
      font-size: 28px;
      font-weight: 700;
      color: #212529;
      line-height: 1.2;
    }

    .stat-detail {
      font-size: 12px;
      color: #adb5bd;
      margin-top: 4px;
    }
  }
}

// Per-channel summary
.channel-summary-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #212529;
    margin: 0 0 16px;
  }

  .channel-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .channel-cell {
    display: flex;
  }

  .channel-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    width: 100%;
    display: flex;
    flex-direction: column;

    .channel-card-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;

      i {
        font-size: 16px;
      }

      .channel-card-title {
        font-size: 15px;
        font-weight: 600;
        color: #212529;
      }

      .included-badge {
        font-size: 11px;
        font-weight: 600;
        color: #0e9bd6;
        background: #e7f6fb;
      }
    }

    .channel-stats {
      display: flex;
      justify-content: space-between;
      margin-bottom: 14px;

      .cstat {
        display: flex;
        flex-direction: column;

        .cstat-val {
          font-size: 20px;
          font-weight: 700;
          color: #212529;
          line-height: 1.2;
        }

        .cstat-label {
          font-size: 11px;
          color: #6c757d;
          text-transform: uppercase;
          margin-top: 2px;
        }
      }
    }

    .channel-rates {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      font-size: 13px;
      color: #6c757d;

      .crate strong {
        color: #212529;
      }
    }

    .channel-credits {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid #e9ecef;
      font-size: 13px;
      font-weight: 600;
      color: #1f7a49;
    }

    .channel-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      padding: 20px 0;
      color: #adb5bd;
      text-align: center;

      i {
        font-size: 28px;
        margin-bottom: 8px;
        opacity: 0.6;
      }

      span {
        font-size: 13px;
      }
    }
  }
}

.charts-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  .chart-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &.full-width {
      grid-column: 1 / -1;
    }

    &.half-width {
      grid-column: span 1;
    }

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      h5 {
        font-size: 14px;
        font-weight: 600;
        color: #212529;
        margin: 0;
      }
    }

    .chart-body {
      height: 250px;
    }

    .empty-chart-state {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #adb5bd;

      i {
        font-size: 40px;
        margin-bottom: 12px;
        opacity: 0.5;
      }

      p {
        margin: 0;
      }
    }
  }
}

.messages-table-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h5 {
      font-size: 16px;
      font-weight: 600;
      color: #212529;
      margin: 0;
    }

    .table-search {
      position: relative;

      i {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: #adb5bd;
      }

      .search-input {
        padding: 8px 12px 8px 36px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        font-size: 14px;
        width: 250px;

        &:focus {
          outline: none;
          border-color: #007bff;
        }
      }
    }
  }

  .messages-table-container {
    overflow-x: auto;
  }

  .messages-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    th {
      font-size: 12px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      background: #f8f9fa;

      &.sortable {
        cursor: pointer;

        &:hover {
          background: #e9ecef;
        }

        i {
          margin-left: 4px;
          font-size: 10px;
        }
      }
    }

    td {
      font-size: 14px;
      color: #212529;
    }

    .clickable-row {
      cursor: pointer;

      &:hover {
        background: #f8f9fa;
      }
    }

    .campaign-name {
      font-weight: 500;
      max-width: 300px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rate-badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;

      &.excellent {
        background: #d4edda;
        color: #155724;
      }

      &.good {
        background: #cce5ff;
        color: #004085;
      }

      &.average {
        background: #fff3cd;
        color: #856404;
      }

      &.low,
      &.poor {
        background: #f8d7da;
        color: #721c24;
      }
    }

    .credits-cell {
      font-size: 13px;
      font-weight: 600;

      &.credits-sms {
        color: #1f7a49;
      }

      &.credits-included {
        color: #0e9bd6;
      }

      &.credits-none {
        color: #adb5bd;
      }
    }

    .action-btn {
      padding: 4px 8px;
      color: #6c757d;

      &:hover {
        color: #212529;
      }
    }
  }

  .empty-table-state {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;

    i {
      font-size: 48px;
      margin-bottom: 12px;
      opacity: 0.5;
    }

    p {
      margin: 0;
    }
  }

  .table-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    .pagination-info {
      font-size: 13px;
      color: #6c757d;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;

      button {
        background: none;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 6px 10px;
        cursor: pointer;

        &:hover:not(:disabled) {
          background: #f8f9fa;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      span {
        font-size: 13px;
        color: #6c757d;
      }
    }
  }
}

@media (max-width: 768px) {
  .statistics-header {
    flex-direction: column;
    gap: 12px;
  }

  .filters-section .filters-row {
    flex-direction: column;

    .filter-group {
      width: 100%;
    }
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .channel-summary-section .channel-grid {
    grid-template-columns: 1fr;
  }

  .charts-section {
    grid-template-columns: 1fr;

    .chart-card.half-width {
      grid-column: 1;
    }
  }
}
</style>
